import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { Task, TaskType } from '@go-modules/interfaces/task';
import { TaskDetailsComponent } from '@go-modules/components/activity-task-sessions/task-details/task-details.component';
import {
	PresentTaskAdditionalSettingsComponent
} from '@go-modules/components/activity-task-sessions/present-task-additional-settings/present-task-additional-settings.component';
import {
	ReactTaskAdditionalSettingsComponent
} from '@go-modules/components/activity-task-sessions/react-task-additional-settings/react-task-additional-settings.component';
import {
	ACTIVITY_EDITOR_CONTEXT,
	ActivityEditorContext
} from '../activity-editor-context';

@Component({
	selector: 'activity-preview-task-panel',
	standalone: true,
	imports: [
		TranslateModule,
		TaskDetailsComponent,
		PresentTaskAdditionalSettingsComponent,
		ReactTaskAdditionalSettingsComponent
	],
	templateUrl: './activity-preview-task-panel.component.html',
	styleUrls: ['./activity-preview-task-panel.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ActivityPreviewTaskPanelComponent {
	private readonly editorContext = inject<ActivityEditorContext>(ACTIVITY_EDITOR_CONTEXT);

	public readonly tasks = computed(() =>
		this.editorContext.tasks()
			.filter((task) => task.type !== TaskType.Evaluate && task.deleted_at == null)
			.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
	);
	public readonly selectedTask = computed(() => this.editorContext.selectedTask());
	public readonly selectedTaskType = this.editorContext.selectedTaskType;
	public readonly totalTaskCount = computed(() =>
		this.tasks().length > 0 ? this.tasks().length + 1 : 0
	);
	public readonly currentTaskPosition = computed(() => {
		const selectedTaskType = this.selectedTaskType();
		const currentIndex = this.tasks().findIndex((task) => task.type === selectedTaskType);
		return currentIndex >= 0 ? currentIndex + 2 : 0;
	});
	public readonly previousButtonLabelKey = computed(() =>
		this.currentTaskPosition() === 2
			? 'task-details_nav-back-to-list'
			: 'task-details_nav-back'
	);
	public readonly shouldShowPresentTaskAdditionalSettings = computed(() =>
		this.selectedTaskType() === TaskType.Present && this.editorContext.isEditMode()
	);
	public readonly shouldShowReactAdditionalSettings = computed(() =>
		this.selectedTaskType() === TaskType.React && this.editorContext.isEditMode()
	);

	public onNavigateBack (): void {
		const previousTask = this.getAdjacentTask(-1);
		if (previousTask == null) {
			this.editorContext.openTaskList?.();
			return;
		}

		this.editorContext.selectTask(previousTask.type);
	}

	public onNavigateForward (): void {
		const nextTask = this.getAdjacentTask(1);
		if (nextTask == null) {
			return;
		}

		this.editorContext.selectTask(nextTask.type);
	}

	public onOpenNavigator (): void {
		this.editorContext.openTaskList?.();
	}

	public onTasksUpdated (tasks: Task[]): void {
		this.editorContext.updateTasks(tasks);
	}

	private getAdjacentTask (direction: -1 | 1): Task | null {
		const selectedTaskType = this.selectedTaskType();
		const currentIndex = this.tasks().findIndex((task) => task.type === selectedTaskType);
		if (currentIndex === -1) {
			return null;
		}

		const targetIndex = currentIndex + direction;
		return this.tasks()[targetIndex] ?? null;
	}
}
